import React, { useState } from 'react';
import axios from 'axios';
import { Phone, Mail, PartyPopper, Music, Monitor, Users, MapPin, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = [
  'Festa privata',
  'Compleanno',
  'Meeting aziendale',
  'Riunione di condominio',
  'Evento musicale',
  'Workshop / Corso',
  'Altro'
];

const FEATURES = [
  { icon: <MapPin size={24} />, title: '200 mq interni', desc: 'Sala ampia e luminosa, perfetta per qualsiasi tipo di evento' },
  { icon: <Music size={24} />, title: 'Impianto Stereo', desc: 'Sistema audio professionale già incluso e pronto all\'uso' },
  { icon: <Monitor size={24} />, title: 'Proiettore', desc: 'Proiettore professionale all\'interno della sala per presentazioni o intrattenimento' },
  { icon: <Users size={24} />, title: '100+ mq esterni', desc: 'Spazio esterno attrezzato per eventi all\'aperto' },
];

export default function RentPage() {
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    event_type: '',
    date: '',
    guests: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/rent-request`, form);
      setSent(true);
      toast.success('Richiesta inviata! Ti contatteremo al più presto.');
    } catch {
      toast.error('Errore nell\'invio. Riprova o contattaci direttamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Hero */}
      <div className="relative bg-gradient-to-br from-[#0288D1] to-[#01579B] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-80 h-80 rounded-full bg-[#FFCA28]" />
          <div className="absolute -bottom-10 -left-10 w-60 h-60 rounded-full bg-white" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-[#FFCA28] text-sm font-bold mb-6">
            <PartyPopper size={16} /> Spazio disponibile per i tuoi eventi
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-tight mb-4">
            Affitta la nostra sala
          </h1>
          <p className="text-blue-100 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Uno spazio versatile e accogliente per ogni occasione: feste, compleanni, meeting, riunioni e molto altro. 
            Tu pensi agli invitati, al resto ci pensiamo noi!
          </p>
        </div>
      </div>

      {/* Features */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 -mt-8 relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-xl shadow-slate-200/40 text-center card-hover">
              <div className="w-12 h-12 rounded-xl bg-[#FF7043]/10 text-[#FF7043] flex items-center justify-center mx-auto mb-3">
                {f.icon}
              </div>
              <h3 className="font-heading font-bold text-base text-slate-900 mb-1">{f.title}</h3>
              <p className="text-xs text-slate-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left: Info */}
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF7043] mb-2">Il nostro spazio</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-6">
              Ideale per <span className="text-[#0288D1]">ogni occasione</span>
            </h2>
            <p className="text-slate-600 leading-relaxed mb-8">
              La nostra struttura offre uno spazio interno di <strong>200 mq</strong> con <strong>impianto stereo professionale</strong> e <strong>proiettore</strong>, 
              e un'area esterna di oltre <strong>100 mq</strong>. 
              L'ambiente è versatile e si adatta facilmente a qualsiasi tipo di evento.
            </p>

            <div className="space-y-4 mb-8">
              <h3 className="font-heading font-bold text-lg text-slate-900">Perfetto per:</h3>
              <div className="grid grid-cols-2 gap-3">
                {['Feste private', 'Compleanni', 'Meeting aziendali', 'Riunioni di condominio', 'Eventi musicali', 'Workshop e corsi', 'Presentazioni', 'E molto altro...'].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle2 size={16} className="text-[#FF7043] shrink-0" />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#FFF8F5] rounded-2xl p-6 border border-orange-100">
              <p className="font-heading font-bold text-slate-900 mb-1">Come funziona?</p>
              <p className="text-sm text-slate-600 leading-relaxed">
                I prezzi vengono concordati direttamente in base al tipo di evento e alla durata. 
                Compila il modulo qui accanto o contattaci direttamente per avere tutte le informazioni!
              </p>
            </div>

            {/* Direct contacts */}
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a
                href="tel:3801599564"
                data-testid="rent-call-btn"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#FF7043] text-white font-heading font-bold rounded-full btn-caribbean"
              >
                <Phone size={18} /> 380 159 9564
              </a>
              <a
                href="mailto:caribbeanbluemoon2025@gmail.com"
                data-testid="rent-email-btn"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#0288D1] text-white font-heading font-bold rounded-full btn-caribbean btn-blue"
              >
                <Mail size={18} /> Scrivici
              </a>
            </div>
          </div>

          {/* Right: Form */}
          <div>
            {sent ? (
              <div className="bg-white rounded-3xl p-8 md:p-10 border border-slate-100 shadow-xl shadow-slate-100/50 text-center" data-testid="rent-success">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 size={40} className="text-green-600" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-slate-900 mb-3">Richiesta Inviata!</h3>
                <p className="text-slate-500 mb-6">
                  Grazie per l'interesse! Ti contatteremo al più presto per concordare i dettagli.
                </p>
                <button
                  onClick={() => { setSent(false); setForm({ name: '', email: '', phone: '', event_type: '', date: '', guests: '', message: '' }); }}
                  className="px-6 py-3 bg-[#0288D1] text-white font-heading font-bold rounded-full btn-caribbean btn-blue"
                >
                  Nuova Richiesta
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-3xl p-6 md:p-8 border border-slate-100 shadow-xl shadow-slate-100/50" data-testid="rent-form-card">
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-1">Richiedi informazioni</h3>
                <p className="text-sm text-slate-500 mb-6">Compila il modulo e ti ricontatteremo!</p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="rent-name">Nome e Cognome *</Label>
                    <Input
                      id="rent-name"
                      data-testid="rent-name"
                      required
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="Mario Rossi"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="rent-email">Email *</Label>
                      <Input
                        id="rent-email"
                        data-testid="rent-email"
                        type="email"
                        required
                        value={form.email}
                        onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                        placeholder="email@esempio.it"
                      />
                    </div>
                    <div>
                      <Label htmlFor="rent-phone">Telefono *</Label>
                      <Input
                        id="rent-phone"
                        data-testid="rent-phone"
                        required
                        value={form.phone}
                        onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                        placeholder="380 123 4567"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Tipo di Evento *</Label>
                    <Select required onValueChange={v => setForm(f => ({ ...f, event_type: v }))}>
                      <SelectTrigger data-testid="rent-event-type">
                        <SelectValue placeholder="Seleziona il tipo di evento" />
                      </SelectTrigger>
                      <SelectContent>
                        {EVENT_TYPES.map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="rent-date">Data indicativa</Label>
                      <Input
                        id="rent-date"
                        data-testid="rent-date"
                        type="date"
                        value={form.date}
                        onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="rent-guests">Numero invitati (circa)</Label>
                      <Input
                        id="rent-guests"
                        data-testid="rent-guests"
                        type="number"
                        min="1"
                        value={form.guests}
                        onChange={e => setForm(f => ({ ...f, guests: e.target.value }))}
                        placeholder="30"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="rent-message">Raccontaci il tuo evento</Label>
                    <Textarea
                      id="rent-message"
                      data-testid="rent-message"
                      value={form.message}
                      onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                      placeholder="Descrivi brevemente cosa hai in mente: tipo di evento, esigenze particolari, orari..."
                      rows={4}
                    />
                  </div>

                  <div className="flex items-start gap-2">
                    <input type="checkbox" id="rent-privacy" required className="mt-1 accent-[#FF7043]" data-testid="rent-privacy-consent" />
                    <label htmlFor="rent-privacy" className="text-xs text-slate-500">
                      Accetto il trattamento dei miei dati personali in conformità alla <a href="/privacy" target="_blank" className="text-[#0288D1] underline">Privacy Policy</a> per la gestione della mia richiesta. *
                    </label>
                  </div>

                  <button
                    type="submit"
                    data-testid="rent-submit-btn"
                    disabled={loading}
                    className="w-full py-3.5 bg-[#FF7043] text-white font-heading font-bold text-base rounded-full btn-caribbean disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading ? 'Invio in corso...' : 'Invia Richiesta'}
                    {!loading && <ArrowRight size={18} />}
                  </button>

                  <p className="text-xs text-slate-400 text-center">
                    Ti ricontatteremo per concordare i dettagli e il prezzo.
                  </p>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
